import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { fetchCurrentUser } from "../../store/slices/misc/currentUser";
import ViewTenderDetails from "./ViewTender";
import { Button, Spinner, Card, Alert } from "reactstrap";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import {
  Block,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockDes,
  PreviewCard,
  Icon,
} from "../../components/Component";
import { message, Spin } from "antd";
import { useForm } from "react-hook-form";
import tenderApis from "../../api/tender/tender";
import Swal from "sweetalert2";
import SupplierRegistration from "./SupplierRegistration";
import OtpInputScreen from "./OtpInputScreen";
import Cookies from "js-cookie";
import LogoDark2x from "../../images/logo-dark2x.png";

interface TenderLinkData {
  tenderId?: number | string;
  id?: number | string;
  tender_id?: number | string;
  tId?: number | string;
  [key: string]: any;
}

const PublicTenderView: React.FC = () => {
  const { linkId } = useParams<{ linkId: string }>();
  const dispatch: any = useDispatch();
  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors },
    watch,
  } = useForm();
  const [loading, setLoading] = useState(false);
  const [tenderLinkData, setTenderLinkData] = useState<TenderLinkData | null>(
    null
  );
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [verifiedTenderId, setVerifiedTenderId] = useState<any>(null);
  const [verifiedSupplierBidStatus, setVerifiedSupplierBidStatus] = useState<string>("New Tender");
  const [verifiedSupplierId, setVerifiedSupplierId] = useState<number | undefined>(undefined);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [pendingEmailOrPhone, setPendingEmailOrPhone] = useState<string>("");
  const [pendingName, setPendingName] = useState<string>("");
  const [pendingEmail, setPendingEmail] = useState<string>("");
  const [pendingPhone, setPendingPhone] = useState<string>("");
  const [otpSent, setOtpSent] = useState(false);
  const [buyerOrgId, setBuyerOrgId] = useState<number | null>(null);
  const [restoringSession, setRestoringSession] = useState(() => !!Cookies.get("access-token"));

  // On page load/refresh, check if access-token exists and restore session
  useEffect(() => {
    if (linkId) {
      verifyPublicLink();
    }
  }, [linkId]);

  useEffect(() => {
    const restoreSession = async () => {
      const accessToken = Cookies.get("access-token");
      if (!accessToken || !tenderLinkData) {
        setRestoringSession(false);
        return;
      }

      try {
        setLoading(true);
        // Fetch current user to restore session
        const userAction = await dispatch(fetchCurrentUser());
        const supplierId = userAction?.payload?.supplierId;

        if (supplierId) {
          const tenderId =
            tenderLinkData?.tenderId ||
            tenderLinkData?.id ||
            tenderLinkData?.tender_id ||
            tenderLinkData?.tId;

          if (tenderId) {
            // Check supplier bid status to get correct status
            try {
              const bidStatusRes = await tenderApis.checkSupplierBidStatus(
                Number(tenderId),
                supplierId
              );
              const bidStatusData =
                bidStatusRes?.data ||
                bidStatusRes?.response?.data ||
                bidStatusRes;
              const status =
                bidStatusData?.status || bidStatusData?.bidStatus || "New Tender";
              setVerifiedSupplierBidStatus(status);
            } catch (e) {
              console.log("checkSupplierBidStatus on restore failed:", e);
            }

            setVerifiedSupplierId(supplierId);
            setVerifiedTenderId(tenderId);
          }
        }
      } catch (error) {
        console.log("Session restore failed, showing login form:", error);
        // Token is invalid/expired, clear it and let user login again
        Cookies.remove("access-token");
        Cookies.remove("refresh-token");
      } finally {
        setRestoringSession(false);
        setLoading(false);
      }
    };

    restoreSession();
  }, [tenderLinkData]);

  const verifyPublicLink = async () => {
    try {
      setLoading(true);
      if (!linkId) {
        Swal.fire("Error", "Link ID is required", "error");
        setLoading(false);
        return;
      }
      const response = await tenderApis.verifyPublicLink(linkId);
      console.log("verifyPublicLink full response:", JSON.stringify(response));

      // Extract tender data from various possible response formats
      let linkData: any = null;

      if (response?.status && response?.response) {
        linkData = response.response;
      } else if (response?.status && response?.data) {
        linkData = response.data;
      } else if (response?.tenderId || response?.id || response?.tender_id || response?.tId) {
        linkData = response;
      }

      // Normalize tenderId from any possible field name
      const resolvedTenderId =
        linkData?.tenderId ||
        linkData?.id ||
        linkData?.tender_id ||
        linkData?.tId;

      console.log("linkData:", JSON.stringify(linkData), "resolvedTenderId:", resolvedTenderId);

      if (resolvedTenderId) {
        // Ensure tenderId is always set regardless of original field name
        const normalizedLinkData = { ...linkData, tenderId: resolvedTenderId };
        if (normalizedLinkData.orgId) {
          setBuyerOrgId(normalizedLinkData.orgId);
        }
        setTenderLinkData(normalizedLinkData);
      } else {
        console.error("No tenderId in link validation response:", response);
        Swal.fire(
          "Error",
          response?.message || "Tender not found or invalid link",
          "error"
        );
      }
    } catch (error: any) {
      console.error("Error verifying public link:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to verify link";
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const sendOtpToPhone = async (phoneNumber: string, email?: string) => {
    try {
      const response = await tenderApis.sendOtp(
        phoneNumber,
        email || pendingEmail || "",
        "Seller"
      );
      if (response?.status === true && response?.message === "success") {
        message.success("OTP sent to your mobile number");
        setOtpSent(true);
        return true;
      } else {
        throw new Error(response?.message || "Failed to send OTP");
      }
    } catch (error: any) {
      console.error("Error sending OTP:", error);
      Swal.fire("Error", error.message || "Failed to send OTP", "error");
      return false;
    }
  };

  const onSubmit = async (values: any) => {
    try {
      setLoading(true);
      const { email, phoneNumber } = values;

      if (!phoneNumber) {
        message.error("Phone number is required");
        setLoading(false);
        return;
      }

      setPendingEmail(email);
      setPendingPhone(phoneNumber);

      try {
        const response = await tenderApis.sendOtp(
          phoneNumber,
          email || "",
          "Seller"
        );

        if (response?.status === true && response?.message === "success") {
          message.success("OTP sent to your mobile number");
          setOtpSent(true);
          setPendingEmailOrPhone(email || phoneNumber);
          setUserInfo({ email, phoneNumber });
          setShowOtpScreen(true);
        } else {
          const errorMessage =
            response?.message ||
            response?.response?.data?.message ||
            response?.response?.message ||
            "";

          const isUserNotFound =
            errorMessage.toLowerCase().includes("user not found") ||
            errorMessage.toLowerCase().includes("usernot found") ||
            errorMessage.toLowerCase().includes("user does not exist");

          if (isUserNotFound) {
            setLoading(false);
            Swal.fire({
              icon: "info",
              title: "Registration Required",
              text: "User not found. Please register to continue.",
              confirmButtonText: "OK",
              confirmButtonColor: "#3085d6",
            }).then((result) => {
              if (result.isConfirmed) {
                setPendingEmailOrPhone(email || phoneNumber);
                setPendingName("");
                setShowRegistrationForm(true);
              }
            });
          } else {
            setLoading(false);
            Swal.fire(
              "Error",
              errorMessage || "Failed to send OTP. Please try again.",
              "error"
            );
          }
        }
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          error?.response?.data?.error ||
          error?.response?.message ||
          "";

        const isUserNotFound =
          errorMessage.toLowerCase().includes("user not found") ||
          errorMessage.toLowerCase().includes("usernot found") ||
          errorMessage.toLowerCase().includes("user does not exist");

        if (isUserNotFound) {
          setLoading(false);
          Swal.fire({
            icon: "info",
            title: "Registration Required",
            text: "User not found. Please register to continue.",
            confirmButtonText: "OK",
            confirmButtonColor: "#3085d6",
          }).then((result) => {
            if (result.isConfirmed) {
              setPendingEmailOrPhone(email || phoneNumber);
              setPendingName("");
              setShowRegistrationForm(true);
            }
          });
        } else {
          setLoading(false);
          Swal.fire(
            "Error",
            errorMessage || "Failed to send OTP. Please try again.",
            "error"
          );
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      Swal.fire("Error", "An error occurred. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRegistrationComplete = async (registrationData: any) => {
    try {
      setLoading(true);
      const emailOrPhone =
        registrationData.email ||
        registrationData.phoneNumber ||
        pendingEmailOrPhone;
      const name = registrationData.contactPerson || pendingName;
      const phoneNumber = registrationData.phoneNumber;

      message.success("Registration completed successfully!");
      setPendingEmail(registrationData.email);
      setPendingPhone(phoneNumber);
      setPendingEmailOrPhone(emailOrPhone);
      const newUserInfo = {
        email: registrationData.email,
        phoneNumber,
        name,
      };
      setUserInfo(newUserInfo);

      if (phoneNumber) {
        const otpSent = await sendOtpToPhone(
          phoneNumber,
          registrationData.email
        );
        if (otpSent) {
          setShowRegistrationForm(false);
          setShowOtpScreen(true);
        }
      }
    } catch (error: any) {
      console.error("Error completing registration:", error);
      Swal.fire(
        "Error",
        error.message || "Failed to complete registration. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerification = async (otp: string) => {
    try {
      setLoading(true);
      const phoneNumber = pendingPhone || userInfo?.phoneNumber;

      if (!phoneNumber) {
        Swal.fire("Error", "Phone number not found", "error");
        return;
      }

      const response = await tenderApis.verifyOtp(phoneNumber, otp);

      console.log("verifyOtp full response:", JSON.stringify(response));

      // Extract token from various possible response formats
      const tokenData =
        response?.data?.token ||
        response?.token ||
        response?.response?.token;

      if (tokenData?.access_token) {
        Cookies.set("access-token", JSON.stringify(tokenData), {
          expires: 7,
          secure: true,
          sameSite: "Strict",
        });
        if (tokenData.refresh_token) {
          Cookies.set("refresh-token", tokenData.refresh_token, {
            expires: 7,
            secure: true,
            sameSite: "Strict",
          });
        }
        // Fetch current user to get supplierId (needed for expressInterest)
        const userAction = await dispatch(fetchCurrentUser());
        const supplierId = userAction?.payload?.supplierId;
        if (supplierId) {
          setVerifiedSupplierId(supplierId);
        }

        message.success("OTP verified successfully!");

        // Render ViewTenderDetails inline with the tender ID
        const tenderId =
          tenderLinkData?.tenderId ||
          tenderLinkData?.id ||
          tenderLinkData?.tender_id ||
          tenderLinkData?.tId;
        if (tenderId) {
          const supplierBidStatus =
            tenderLinkData?.supplierBidStatus || "New Tender";
          setVerifiedTenderId(tenderId);
          setVerifiedSupplierBidStatus(supplierBidStatus);
        } else {
          Swal.fire("Error", "Tender ID not found", "error");
        }
      } else if (response?.status === false || !tokenData) {
        throw new Error(response?.message || "Invalid OTP");
      }
    } catch (error: any) {
      console.error("Error verifying OTP:", error);
      Swal.fire(
        "Error",
        error.message || "Invalid OTP. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    const phoneNumber = pendingPhone || userInfo?.phoneNumber;
    const email = pendingEmail || userInfo?.email;
    if (phoneNumber) {
      await sendOtpToPhone(phoneNumber, email);
    }
  };

  // After OTP verification — render ViewTenderDetails inline, URL stays the same
  if (verifiedTenderId) {
    return (
      <ViewTenderDetails
        tenderIdProp={verifiedTenderId}
        supplierBidStatusProp={verifiedSupplierBidStatus}
        supplierIdProp={verifiedSupplierId}
      />
    );
  }

  if ((loading && !tenderLinkData) || restoringSession) {
    return (
      <>
        <Head title="Public Tender" />
        <Content>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "60vh" }}
          >
            <Spin size="large" />
          </div>
        </Content>
      </>
    );
  }

  if (!tenderLinkData) {
    return (
      <>
        <Head title="Public Tender" />
        <Content>
          <Card className="card-bordered">
            <Alert color="danger" className="alert-icon">
              <Icon name="alert-circle" />
              <strong>Tender Not Found</strong>
              <p className="mb-0">
                The tender you are looking for does not exist or has been
                removed.
              </p>
            </Alert>
          </Card>
        </Content>
      </>
    );
  }

  if (showOtpScreen) {
    return (
      <>
        <Head title="OTP Verification" />
        <div className="login-container pt-2">
          <div
            style={{ position: "relative", zIndex: 10, pointerEvents: "auto" }}
          >
            <Block className="nk-block-middle nk-auth-body wide-xs">
              <div className="brand-logo pb-4 text-center">
                <Link to={process.env.PUBLIC_URL + "/"} className="logo-link">
                  <img className="" src={LogoDark2x} alt="logo" />
                </Link>
              </div>
              <PreviewCard
                className="card-bordered"
                bodyClass="card-inner-lg"
                style={{
                  position: "relative",
                  zIndex: 10,
                  pointerEvents: "auto",
                }}
              >
                <BlockHead>
                  <BlockHeadContent>
                    <BlockTitle tag="h4">OTP Verification</BlockTitle>
                    <BlockDes>
                      <p>
                        Enter the OTP sent to your mobile number{" "}
                        <strong>{pendingPhone || userInfo?.phoneNumber}</strong>
                      </p>
                    </BlockDes>
                  </BlockHeadContent>
                </BlockHead>
                <Block>
                  <div
                    className="alert alert-info alert-icon"
                    style={{ marginBottom: "24px" }}
                  >
                    <Icon name="info" />
                    <strong>OTP Sent</strong> Please check your mobile number
                    for the OTP.
                  </div>
                  <OtpInputScreen
                    onVerify={handleOtpVerification}
                    onResend={handleResendOtp}
                    onBack={() => {
                      setShowOtpScreen(false);
                      setOtpSent(false);
                      setPendingEmail("");
                      setPendingPhone("");
                      setUserInfo(null);
                    }}
                    phoneNumber={pendingPhone || userInfo?.phoneNumber}
                    loading={loading}
                  />
                </Block>
              </PreviewCard>
            </Block>
          </div>
          <div className="login-bgi"></div>
        </div>
      </>
    );
  }

  if (showRegistrationForm) {
    return (
      <>
        <Head title="Supplier Registration" />
        <div className="login-container pt-2">
          <div className="d-flex justify-content-center">
            <div
              style={{
                width: "100%",
                maxWidth: "1200px",
                position: "relative",
                zIndex: 10,
                pointerEvents: "auto",
              }}
            >
              <SupplierRegistration
                onComplete={handleRegistrationComplete}
                onBack={() => {
                  setShowRegistrationForm(false);
                  setPendingEmailOrPhone("");
                  setPendingName("");
                }}
                initialEmailOrPhone={pendingEmailOrPhone}
                initialName={pendingName}
                buyerOrgId={buyerOrgId}
              />
            </div>
          </div>
          <div className="login-bgi"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <Head title="Public Tender" />
      <div className="login-container pt-2">
        <div
          style={{ position: "relative", zIndex: 10, pointerEvents: "auto" }}
        >
          <Block className="nk-block-middle nk-auth-body wide-xs">
            <div className="brand-logo pb-4 text-center">
              <Link to={process.env.PUBLIC_URL + "/"} className="logo-link">
                <img className="" src={LogoDark2x} alt="logo" />
              </Link>
            </div>
            <PreviewCard
              className="card-bordered"
              bodyClass="card-inner-lg"
              style={{
                position: "relative",
                zIndex: 10,
                pointerEvents: "auto",
              }}
            >
              <BlockHead>
                <BlockHeadContent>
                  <BlockTitle tag="h4">Tender Access</BlockTitle>
                  <BlockDes>
                    <p>
                      Access Tender -{" "}
                      <strong>
                        {tenderLinkData?.tenderNo ||
                          tenderLinkData?.tenderId ||
                          tenderLinkData?.id}
                      </strong>
                    </p>
                  </BlockDes>
                </BlockHeadContent>
              </BlockHead>
              <Block>
                <div
                  className="alert alert-info alert-icon"
                  style={{ marginBottom: "24px" }}
                >
                  <Icon name="info" />
                  <strong>Enter your email and phone number.</strong> We'll
                  check if you have an account. If not, we'll create one for
                  you.
                </div>
                <form
                  className="is-alter"
                  onSubmit={handleFormSubmit(onSubmit)}
                  style={{
                    position: "relative",
                    zIndex: 10,
                    pointerEvents: "auto",
                  }}
                >
                  <div className="form-group">
                    <div className="form-label-group">
                      <label className="form-label" htmlFor="email">
                        Email
                      </label>
                    </div>
                    <div className="form-control-wrap">
                      <input
                        type="email"
                        id="email"
                        {...register("email", {
                          required: "Please enter your email",
                          pattern: {
                            value:
                              /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,24}$/,
                            message: "Please enter a valid email address",
                          },
                        })}
                        placeholder="Enter email address"
                        className="form-control-lg form-control"
                        autoComplete="off"
                        style={{ pointerEvents: "auto", height: "48px" }}
                      />
                      {errors.email && (
                        <span className="invalid">
                          {errors.email.message as string}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="form-label-group">
                      <label className="form-label" htmlFor="phoneNumber">
                        Phone Number
                      </label>
                    </div>
                    <div className="form-control-wrap">
                      <input
                        type="tel"
                        id="phoneNumber"
                        {...register("phoneNumber", {
                          required: "Please enter your phone number",
                          pattern: {
                            value: /^[6-9]\d{9}$/,
                            message:
                              "Please enter a valid 10-digit phone number",
                          },
                        })}
                        placeholder="Enter phone number"
                        className="form-control-lg form-control"
                        autoComplete="off"
                        style={{ pointerEvents: "auto", height: "48px" }}
                        maxLength={10}
                        onInput={(e: any) => {
                          e.target.value = e.target.value.replace(
                            /[^0-9]/g,
                            ""
                          );
                        }}
                      />
                      {errors.phoneNumber && (
                        <span className="invalid">
                          {errors.phoneNumber.message as string}
                        </span>
                      )}
                      <div className="form-note mt-2">
                        <Icon name="info" className="text-info" /> OTP will be
                        shared to the mobile number
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <Button
                      type="submit"
                      color="primary"
                      size="lg"
                      className="btn-block"
                      disabled={loading}
                      style={{
                        pointerEvents: "auto",
                        cursor: loading ? "not-allowed" : "pointer",
                      }}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" color="light" className="me-2" />
                          Processing...
                        </>
                      ) : (
                        "Continue"
                      )}
                    </Button>
                  </div>
                  <div className="form-group text-center mt-3">
                    <div className="form-note">
                      Don't have an account?{" "}
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          const formValues = watch();
                          setPendingEmailOrPhone(
                            formValues.email || formValues.phoneNumber || ""
                          );
                          setPendingName("");
                          setShowRegistrationForm(true);
                        }}
                        style={{
                          color: "#5263FF",
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                      >
                        Register here
                      </a>
                    </div>
                  </div>
                </form>
              </Block>
            </PreviewCard>
          </Block>
        </div>
        <div className="login-bgi"></div>
      </div>
    </>
  );
};

export default PublicTenderView;
